import type { DerivedVitals } from '@dru/clinical-math/derived-vitals';
import { InfoTip, cn } from '@dru/ui';

/**
 * What the height, weight and blood pressure work out to, beside the
 * readings they came from.
 *
 * Shown at the vitals station as the values are typed, and to the physician
 * on the review. None of it is a measurement: no reference range, no flag,
 * no place in the counts. The one figure that is also stored — BMI — is
 * recorded by the API from the same formula, so what the technician sees
 * here is what the record holds.
 */
export function DerivedVitalsPanel({ derived, compact }: { derived: DerivedVitals; compact?: boolean }) {
  if (!derived.any) return null;
  const b = derived.body, bp = derived.bloodPressure;
  const body: Array<[string, string | null, string?]> = [
    ['BMI', b.bmi == null ? null : `${b.bmi} kg/m²`, b.bmiBand?.label],
    ['Resting energy', b.bmrKcalPerDay == null ? null : `${Math.round(b.bmrKcalPerDay)} kcal/day`],
    ['Ideal weight', b.idealWeightKg == null ? null : `${b.idealWeightKg} kg`],
    ['Body surface', b.bodySurfaceM2 == null ? null : `${b.bodySurfaceM2} m²`],
    ['Water', b.waterLitresPerDay == null ? null : `${b.waterLitresPerDay} L/day`],
    ['Steps', b.stepTargetPerDay == null ? null : `${b.stepTargetPerDay.toLocaleString('en-IN')} /day`],
    ['Exercise heart rate', b.targetHeartRate ? `${b.targetHeartRate.low}–${b.targetHeartRate.high} /min` : null],
  ];
  const pressure: Array<[string, string | null, string?]> = [
    ['Band', bp.band?.label ?? null, bp.band?.note],
    ['Mean arterial', bp.meanArterialPressure == null ? null : `${bp.meanArterialPressure} mmHg`],
    ['Pulse pressure', bp.pulsePressure == null ? null : `${bp.pulsePressure} mmHg`],
  ];
  const groups: Array<[string, typeof body]> = [
    ['Body measurements', body.filter((r) => r[1] != null)],
    ['Blood pressure', pressure.filter((r) => r[1] != null)],
  ].filter((g) => g[1].length > 0) as Array<[string, typeof body]>;
  if (!groups.length) return null;

  return (
    <div className={cn('bg-neutral-50 px-4', compact ? 'py-2.5' : 'py-3')} data-testid="derived-vitals">
      <div className="mb-2 flex items-center gap-1">
        <span className="text-caption font-medium uppercase tracking-wide text-neutral-600">Worked out from the readings</span>
        <InfoTip label="About these figures">
          Arithmetic on the height, weight and blood pressure recorded here. BMI is saved by the system from height and
          weight; the rest is shown for reading and is not stored, flagged or counted. One reading is never a diagnosis.
        </InfoTip>
      </div>
      <div className={cn('grid gap-x-6 gap-y-2', compact ? 'sm:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-3')}>
        {groups.map(([title, rows]) => (
          <dl key={title} className="min-w-0">
            <dt className="text-micro uppercase tracking-wide text-neutral-500">{title}</dt>
            {rows.map(([name, value, note]) => (
              <div key={name} className="flex flex-wrap items-baseline gap-x-2 py-0.5">
                <span className="text-small text-neutral-700">{name}</span>
                <span className="text-small font-medium tabular-nums text-neutral-900">{value}</span>
                {note && <span className="w-full text-caption text-neutral-600">{note}</span>}
              </div>
            ))}
          </dl>
        ))}
      </div>
    </div>
  );
}
